import React, { useState } from 'react';
import { Button, Form, Input, Modal, Tooltip, message } from 'antd';
import { BookOutlined } from '@ant-design/icons';
import { useCreateTeamAssistantMemory } from '../../hooks/useTeamAssistantQueries';

const { TextArea } = Input;

/**
 * Propose a durable team memory from a chat message (candidate until admin confirms).
 */
const TeamAssistantRememberMessageAction = ({ message: chatMessage, size = 'small', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const createMutation = useCreateTeamAssistantMemory();

  const numericId =
    chatMessage?.id && !String(chatMessage.id).startsWith('temp-') ? Number(chatMessage.id) : null;
  const text = (chatMessage?.content || '').trim();
  const canUse = Boolean(text) && numericId;

  const openModal = () => {
    form.setFieldsValue({
      memory: text.length > 2000 ? `${text.slice(0, 1997)}…` : text,
      category: '',
    });
    setOpen(true);
  };

  const onSubmit = async (values) => {
    try {
      await createMutation.mutateAsync({
        memory: values.memory,
        category: values.category || undefined,
        source_message_id: numericId,
      });
      message.success('Saved as candidate — an admin must confirm under Team memory before it affects replies.');
      setOpen(false);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Could not save');
    }
  };

  if (!canUse) {
    return null;
  }

  return (
    <>
      <Tooltip title="Save as team memory — links this chat message automatically">
        <Button
          type="text"
          size={size}
          className={`message-copy-share-actions-btn ${className}`.trim()}
          icon={<BookOutlined />}
          aria-label="Propose team memory"
          onClick={openModal}
        />
      </Tooltip>
      <Modal
        title="Propose team memory"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending}
        okText="Submit candidate"
        destroyOnClose
      >
        <p style={{ marginBottom: 12, color: '#595959', fontSize: 13 }}>
          Save a short insurance fact for the whole team. Confirmed memories are included in every assistant
          reply. This will be linked to the chat message you bookmarked — no ID to remember.
        </p>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item
            name="memory"
            label="Fact to remember"
            rules={[{ required: true, message: 'Enter the fact' }]}
          >
            <TextArea
              rows={4}
              placeholder="e.g. XYZ carrier requires signed ACORD 125 before quoting commercial auto in FL…"
            />
          </Form.Item>
          <Form.Item name="category" label="Category (optional)" tooltip="Tag for grouping — Carriers, LOB, Underwriting, Process…">
            <Input placeholder="e.g. Carriers" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TeamAssistantRememberMessageAction;
